import type {Database} from '$lib/classes/interfaces/database';
import type {Functions} from '$lib/classes/services/functions';
import {timer} from '$lib/utils/timer';
import type {SubscriptionDoc} from '$shared/subscription-doc';
import type {ManageSubscriptionLinks} from '$lib/classes/_types/manage-subscription-links';

const legacyPrices = [
    import.meta.env.VITE_PADDLE_PRO_BRONZE_PRICE_ID,
    import.meta.env.VITE_PADDLE_PRO_SILVER_PRICE_ID,
    import.meta.env.VITE_PADDLE_PRO_GOLD_PRICE_ID,
    import.meta.env.VITE_PADDLE_PRO_PLATINUM_PRICE_ID,
]

export class Subscription {

    private _doc: SubscriptionDoc | null = $state(null)
    get doc() { return this._doc }

    hasActiveSubscription = $derived(this._doc !== null)

    isLegacySubscription = $derived(Boolean(this._doc && legacyPrices.includes(this._doc.items[0]?.priceId)))

    private cancelDocSubscription = () => {}

    // NOTE setting promise prevents multiple calls to getPortalSession while loading
    private manageSubscriptionLinksPromise: Promise<ManageSubscriptionLinks> | null = null

    constructor(
        private readonly database: Database,
        private readonly functions: Functions
    ) {}

    private resetState() {
        this._doc = null
    }

    async connect(uid: string): Promise<void> {
        const timer_subscription = timer('subscription.init')

        try {
            const {unsubscribe, firstValue} = this.database.subscribeSubscriptionDoc(uid, doc => {
                const validSubscriptionStatuses = ['active', 'past_due', 'trialing']
                if (doc && validSubscriptionStatuses.includes(doc.status)) {
                    if (doc.items.length !== 1) {
                        console.error(`ERROR incorrect subscription item count ${doc.items.length} subscriptionId ${doc.id}`)
                    }
                    if (doc.items.length === 0) {
                        this.resetState()
                    } else {
                        this._doc = doc
                    }
                } else {
                    this.resetState()
                }
            })

            this.cancelDocSubscription = unsubscribe
            await firstValue

        } catch (error) {
            throw new Error('ERROR subscription.connect - subscribeSubscriptionDoc', {cause: error})
        }

        timer_subscription()
    }

    disconnect() {
        this.cancelDocSubscription()
        this.cancelDocSubscription = () => {}
        this.resetState()
    }

    async getManageSubscriptionLinks(): Promise<ManageSubscriptionLinks> {
        if (this.doc === null) {
            //QUESTION common to throw error if method was called when it shouldn't (without active subscription)?
            throw new Error('ERROR subscription.getManageSubscriptionLinks() - subscription === null')
        }
        // NOTE once set the promise is never reset >> links would probably expire >> prevention of multiple calls really necessary?
        //  no information on expiration in Paddle docs or response from API
        if (this.manageSubscriptionLinksPromise === null) {
            const customerId = this.doc.customerId
            const subscriptionId = this.doc.id
            this.manageSubscriptionLinksPromise = this.functions.getPortalSession(customerId, subscriptionId)
        }
        try {
            return await this.manageSubscriptionLinksPromise
        } catch (error) {
            throw new Error('ERROR getting portal session', {cause: error})
        }
    }

    getPlanName(subscription: SubscriptionDoc | null) {
        // NOTE subscription as parameter to make function call reactive >> should be redundant with Svelte5
        if (subscription === null) {
            console.error('ERROR subscription.getPlan() - subscription === null')
            return ''
        }
        if (this.isLegacySubscription) {
            const monthsTotal = subscription.previouslyPrepaidMonths + subscription.monthsSubscribed
            return monthsTotal > 12 ? 'Platinum'
                : monthsTotal > 6 ? 'Gold'
                    : monthsTotal > 3 ? 'Silver'
                        : 'Bronze'
        } else {
            // without 'Pro »' prefix for styling, just 'Flex/Plus/Max'
            return subscription.items[0]?.priceName?.slice(6) ?? ''
        }
    }
}